import { Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { AuthProvider, useAuth, GoogleSilentRefresh } from './hooks/useAuth';
import Shell from './components/Shell';
import ScorecardPage from './pages/ScorecardPage';
import DeliverablesPage from './pages/DeliverablesPage';
import TeamPage from './pages/TeamPage';
import AdminOverviewPage from './pages/AdminOverviewPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminPeriodsPage from './pages/AdminPeriodsPage';
import AdminRefreshPage from './pages/AdminRefreshPage';
import AdminAuditPage from './pages/AdminAuditPage';
import AdminTiersPage from './pages/AdminTiersPage';
import AdminCommissionsPage from './pages/AdminCommissionsPage';
import AdminCostRatesPage from './pages/AdminCostRatesPage';
import AdminCommissionReportPage from './pages/AdminCommissionReportPage';
import AdminReportsPage from './pages/AdminReportsPage';
import AdminHygienePage from './pages/AdminHygienePage';
import AdminAtRiskPage from './pages/AdminAtRiskPage';
import AdminForecastPage from './pages/AdminForecastPage';
import AdminDeliverableRollupPage from './pages/AdminDeliverableRollupPage';
import AdminEngagementBudgetPage from './pages/AdminEngagementBudgetPage';
import AdminEngagementWorkspacePage from './pages/AdminEngagementWorkspacePage';
import AdminEngagementsIndexPage from './pages/AdminEngagementsIndexPage';
import AdminInvoicingPage from './pages/AdminInvoicingPage';
import AdminSalesOverlayPage from './pages/AdminSalesOverlayPage';
import AdminClawbacksPage from './pages/AdminClawbacksPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AdminPermissionProfilesPage from './pages/AdminPermissionProfilesPage';
import AdminOrgChartPage from './pages/AdminOrgChartPage';
import AdminBillingPage from './pages/AdminBillingPage';
import AdminBillingReceivablesPage from './pages/AdminBillingReceivablesPage';
import AdminBillingRunDetailPage from './pages/AdminBillingRunDetailPage';
import AdminWebhooksPage from './pages/AdminWebhooksPage';
import AdminFpaAccessPage from './pages/AdminFpaAccessPage';
import AdminFpaActualsPage from './pages/AdminFpaActualsPage';
import AdminFpaQboPage from './pages/AdminFpaQboPage';
import AdminFpaQboCallbackPage from './pages/AdminFpaQboCallbackPage';
import AdminFpaDashboardPage from './pages/AdminFpaDashboardPage';
import AdminFpaHomePage from './pages/AdminFpaHomePage';
import AdminFpaPlansPage from './pages/AdminFpaPlansPage';
import AdminFpaPlanWorkspacePage from './pages/AdminFpaPlanWorkspacePage';
import AdminFpaVariancePage from './pages/AdminFpaVariancePage';
import AdminFpaPredictiveModelPage from './pages/AdminFpaPredictiveModelPage';
import AdminFpaClosePage from './pages/AdminFpaClosePage';
import DashboardPage from './pages/DashboardPage';
import SalesDashboardPage from './pages/SalesDashboardPage';
import AdminSalesQuotasPage from './pages/AdminSalesQuotasPage';
import AdminDealOnboardingPage from './pages/AdminDealOnboardingPage';
import GanttPage from './pages/GanttPage';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

function LoginScreen({ sessionExpired }: { sessionExpired: boolean }) {
  return (
    <div style={{
      // flex: 1 fills the width of #root (which is a flex row per index.html).
      // Without it the grid container collapses to content width and slides
      // to the left edge.
      flex: 1, minHeight: '100vh', display: 'grid', placeItems: 'center',
      background: 'var(--color-bg)', padding: 20,
    }}>
      <div style={{
        width: '100%', maxWidth: 400,
        background: 'var(--color-surface)',
        border: '0.5px solid var(--color-border)',
        borderRadius: 8,
        padding: '40px 32px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
      }}>
        <img
          src="/logo.png"
          alt="Unlimited"
          style={{ maxWidth: 200, height: 'auto', maxHeight: 60 }}
        />
        {sessionExpired && (
          <div style={{
            width: '100%',
            background: 'var(--color-amber-bg)', color: 'var(--color-amber-text)',
            border: '0.5px solid var(--color-amber)',
            borderRadius: 'var(--radius-sm)',
            padding: '8px 12px', fontSize: 12, textAlign: 'center',
          }}>
            Your session timed out. Please sign in again to continue.
          </div>
        )}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--color-text)' }}>
            Operational Reporting Platform
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
            Sign in with your company Google account to continue
          </div>
        </div>
        <GoogleLogin
          onSuccess={(resp) => {
            const handler = (window as unknown as Record<string, unknown>).__orpHandleGoogleLogin;
            if (typeof handler === 'function') handler(resp);
          }}
          onError={() => alert('Google sign-in failed')}
          theme="outline"
          size="large"
        />
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      flex: 1, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--color-text-muted)', fontSize: 13,
    }}>
      Signing in…
    </div>
  );
}

function AppRoutes() {
  const { user, isLoading, needsLogin, sessionExpired } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (needsLogin) return <LoginScreen sessionExpired={sessionExpired} />;

  // Access derives from the effective permission set resolved by /auth/me
  // (role-default profile + assigned profiles + legacy flags) — the same keys
  // the API gates check, definable in Admin → Permission profiles. Reports
  // and admin surfaces are one key per page (is_admin users hold every key).
  // FP&A is the deliberate exception: it stays on the explicit fpa_role grant.
  const has = (key: string) => user?.permissions?.includes(key) === true;
  const canAccessDealOnboarding = has('deal_onboarding');
  const canAccessSalesQuotas = has('sales_manage');
  // The Users page doubles as the view-as target picker, so `impersonate`
  // opens it too (read-only there; edits need admin_users server-side).
  const canAccessUsers = has('admin_users') || has('impersonate');
  // FP&A routes gate on the explicit fpa_role grant, mirroring the server gates.
  // is_admin does NOT grant any of these.
  const canAccessFpa = !!user?.fpa_role;                                            // Actuals (viewer+)
  const isFpaWriter = user?.fpa_role === 'analyst' || user?.fpa_role === 'approver'; // QBO mapping
  const isFpaApprover = user?.fpa_role === 'approver';                              // access management
  const canAccessDashboard = has('exec_dashboard');
  // Sales dashboard — anyone "in sales" (linked HubSpot owner, set by /auth/me)
  // plus anyone granted the sales permissions. Per-rep visibility is scoped
  // server-side: reps see only their own detail.
  const canAccessSales = user?.is_sales === true || has('sales_view') || canAccessSalesQuotas;

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/scorecard" replace />} />
        {canAccessDashboard && <Route path="/dashboard" element={<DashboardPage />} />}
        {canAccessSales && <Route path="/sales" element={<SalesDashboardPage />} />}
        {canAccessSalesQuotas && <Route path="/sales/quotas" element={<AdminSalesQuotasPage />} />}
        {canAccessDealOnboarding && <Route path="/deal-onboarding" element={<AdminDealOnboardingPage />} />}
        <Route path="/scorecard" element={<ScorecardPage />} />
        <Route path="/deliverables" element={<DeliverablesPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/gantt" element={<GanttPage />} />
        {has('report_engagement_budget') && <Route path="/admin/engagement-budget" element={<AdminEngagementBudgetPage />} />}
        {/* The picker, then the workspace. Section is part of the URL so a
            view can be linked to directly. */}
        {has('report_engagement_workspace') && <Route path="/admin/engagements" element={<AdminEngagementsIndexPage />} />}
        {has('report_engagement_workspace') && <Route path="/admin/engagements/:id" element={<AdminEngagementWorkspacePage />} />}
        {has('report_engagement_workspace') && <Route path="/admin/engagements/:id/w/:section?" element={<AdminEngagementWorkspacePage />} />}
        {has('report_invoicing') && <Route path="/admin/invoicing" element={<AdminInvoicingPage />} />}
        {has('report_commissions') && <Route path="/admin/reports/commissions" element={<AdminCommissionReportPage />} />}
        {has('report_hygiene') && <Route path="/admin/hygiene" element={<AdminHygienePage />} />}
        {has('report_at_risk') && <Route path="/admin/at-risk" element={<AdminAtRiskPage />} />}
        {has('report_org_overview') && (
          <>
            <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
            <Route path="/admin/overview" element={<AdminOverviewPage />} />
          </>
        )}
        {has('report_company') && <Route path="/admin/reports" element={<AdminReportsPage />} />}
        {has('report_forecast') && <Route path="/admin/forecast" element={<AdminForecastPage />} />}
        {has('report_deliverable_rollup') && <Route path="/admin/deliverable-rollup" element={<AdminDeliverableRollupPage />} />}
        {has('report_org_chart') && <Route path="/admin/org-chart" element={<AdminOrgChartPage />} />}
        {canAccessUsers && <Route path="/admin/users" element={<AdminUsersPage />} />}
        {(has('admin_periods') || has('admin_commission_periods')) && <Route path="/admin/periods" element={<AdminPeriodsPage />} />}
        {has('admin_refresh') && <Route path="/admin/refresh" element={<AdminRefreshPage />} />}
        {has('admin_audit') && <Route path="/admin/audit" element={<AdminAuditPage />} />}
        {has('admin_tiers') && <Route path="/admin/tiers" element={<AdminTiersPage />} />}
        {/* Readable with the profitability key too — a margin reader needs to
            see the rate their numbers were divided by. */}
        {(has('admin_cost_rates') || has('report_profitability')) && <Route path="/admin/cost-rates" element={<AdminCostRatesPage />} />}
        {has('admin_commission_rates') && <Route path="/admin/commissions" element={<AdminCommissionsPage />} />}
        {has('admin_clawbacks') && <Route path="/admin/clawbacks" element={<AdminClawbacksPage />} />}
        {has('admin_settings') && <Route path="/admin/settings" element={<AdminSettingsPage />} />}
        {has('admin_permission_profiles') && <Route path="/admin/permission-profiles" element={<AdminPermissionProfilesPage />} />}
        {has('sales_overlay') && <Route path="/admin/sales-overlay" element={<AdminSalesOverlayPage />} />}
        {has('billing_imports') && (
          <>
            <Route path="/admin/billing" element={<AdminBillingPage />} />
            <Route path="/admin/billing/imports/:id" element={<AdminBillingRunDetailPage />} />
          </>
        )}
        {has('billing_receivables') && <Route path="/admin/billing/receivables" element={<AdminBillingReceivablesPage />} />}
        {has('billing_webhooks') && <Route path="/admin/webhooks" element={<AdminWebhooksPage />} />}
        {canAccessFpa && (
          <>
            <Route path="/admin/fpa/home" element={<AdminFpaHomePage />} />
            <Route path="/admin/fpa/dashboard" element={<AdminFpaDashboardPage />} />
            <Route path="/admin/fpa/plans" element={<AdminFpaPlansPage />} />
            <Route path="/admin/fpa/plans/:id" element={<AdminFpaPlanWorkspacePage />} />
            <Route path="/admin/fpa/plans/:id/w/:section?" element={<AdminFpaPlanWorkspacePage />} />
            <Route path="/admin/fpa/actuals" element={<AdminFpaActualsPage />} />
            <Route path="/admin/fpa/variance" element={<AdminFpaVariancePage />} />
            <Route path="/admin/fpa/predictive" element={<AdminFpaPredictiveModelPage />} />
            <Route path="/admin/fpa/close" element={<AdminFpaClosePage />} />
          </>
        )}
        {isFpaWriter && (
          <Route path="/admin/fpa/qbo" element={<AdminFpaQboPage />} />
        )}
        {isFpaApprover && (
          <>
            <Route path="/admin/fpa/access" element={<AdminFpaAccessPage />} />
            <Route path="/admin/fpa/qbo/callback" element={<AdminFpaQboCallbackPage />} />
          </>
        )}
        <Route path="*" element={<Navigate to="/scorecard" replace />} />
      </Routes>
    </Shell>
  );
}

function AppWithAuth() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default function App() {
  // When VITE_GOOGLE_CLIENT_ID is set, wrap in GoogleOAuthProvider so the
  // GoogleLogin component works. When unset (dev without SSO), skip the
  // wrapper — the dev persona switcher handles auth instead.
  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <GoogleSilentRefresh />
          <AppRoutes />
        </AuthProvider>
      </GoogleOAuthProvider>
    );
  }
  return <AppWithAuth />;
}
